import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { commissions, operations, products, type Broker } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatDate,
  formatDocument,
  formatMoney,
  formatRate,
} from '@/shared/lib/format';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Modal } from '@/shared/ui/Modal';
import { Spinner } from '@/shared/ui/Spinner';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';

import { DocumentsPanel } from '../documents/DocumentsPanel';
import { descreveConta } from './contaBancaria';

/**
 * Visao rapida do corretor, com o que se pergunta sobre ele. O cadastro completo,
 * com as abas do OnPoint, fica em /corretores/:brokerId (BrokerDetailPage).
 *
 * As tres abas respondem perguntas distintas: quem e, o que trouxe e quanto tem a
 * receber. Sao os tres assuntos que fazem alguem abrir o cadastro de um corretor, e
 * separa-los evita a tela unica que mostra tudo e nao responde nada. A quarta,
 * Documentos, e o painel comum de documentos do repositorio.
 */

type Aba = 'dados' | 'operacoes' | 'comissoes' | 'documentos';

export interface BrokerDrawerProps {
  corretor: Broker;
  podeAtualizar: boolean;
  podeExcluir: boolean;
  movendo: boolean;
  onMover: (etapa: string) => void;
  onEditar: () => void;
  onExcluir: () => void;
  onClose: () => void;
}

/** Rotulo do tipo de chave, para leitura. */
const tiposDeChave: Record<string, string> = {
  cnpj: 'CNPJ',
  cpf: 'CPF',
  email: 'E-mail',
  telefone: 'Telefone',
  aleatoria: 'Chave aleatoria',
};

export function BrokerDrawer({
  corretor,
  podeAtualizar,
  podeExcluir,
  movendo,
  onMover,
  onEditar,
  onExcluir,
  onClose,
}: BrokerDrawerProps) {
  const { can } = useAuth();
  const [aba, setAba] = useState<Aba>('dados');

  // As operacoes e as comissoes do corretor sao consultadas ao servidor com o
  // recorte ja aplicado. Trazer tudo e filtrar na tela devolveria a carteira inteira
  // para mostrar um punhado de linhas.
  const listaOperacoes = useQuery({
    queryKey: ['operations', 'por-corretor', corretor.id],
    queryFn: ({ signal }) =>
      operations.list({ broker_id: corretor.id, limit: 50 }, signal),
    enabled: aba === 'operacoes',
  });

  const listaComissoes = useQuery({
    queryKey: ['commissions', 'por-corretor', corretor.id],
    queryFn: ({ signal }) =>
      commissions.list({ broker_id: corretor.id, limit: 50 }, signal),
    enabled: aba === 'comissoes',
  });

  const listaProdutos = useQuery({
    queryKey: ['products', 'options'],
    queryFn: ({ signal }) => products.list(signal),
    enabled: aba === 'dados' && corretor.product_ids.length > 0,
  });

  const habilitados = (listaProdutos.data ?? []).filter((produto) =>
    corretor.product_ids.includes(produto.id),
  );

  const abas: { chave: Aba; rotulo: string }[] = [
    { chave: 'dados', rotulo: 'Dados' },
    { chave: 'operacoes', rotulo: 'Operacoes' },
    { chave: 'comissoes', rotulo: 'Comissoes' },
    // Painel comum de documentos (P-DOC), para quem le o repositorio.
    ...(can('documentos.documento.view')
      ? [{ chave: 'documentos' as const, rotulo: 'Documentos' }]
      : []),
  ];

  return (
    <Modal
      open
      onClose={onClose}
      title={corretor.legal_name}
      description={formatDocument(corretor.document)}
      size="lg"
      footer={
        <>
          {podeExcluir ? (
            <Button variant="danger" className="mr-auto" onClick={onExcluir}>
              Excluir
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
          {/* A gaveta e a visao rapida. O cadastro completo, com as abas do OnPoint,
              fica na pagina do corretor. */}
          <Link
            to={`/corretores/${corretor.id}`}
            className="inline-flex h-10 items-center rounded-md border border-line px-4 text-sm font-medium text-ink-800 hover:bg-ink-100"
          >
            Abrir cadastro
          </Link>
          {podeAtualizar ? <Button onClick={onEditar}>Editar cadastro</Button> : null}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div
          role="tablist"
          aria-label="Detalhe do corretor"
          className="flex gap-1 border-b border-line"
        >
          {abas.map((item) => (
            <button
              key={item.chave}
              type="button"
              role="tab"
              id={`aba-${item.chave}`}
              aria-selected={aba === item.chave}
              aria-controls={`painel-${item.chave}`}
              onClick={() => setAba(item.chave)}
              className={
                aba === item.chave
                  ? 'border-b-2 border-brand-500 px-3 py-2 text-xs font-semibold text-ink-900'
                  : 'border-b-2 border-transparent px-3 py-2 text-xs text-ink-500 hover:text-ink-900'
              }
            >
              {item.rotulo}
            </button>
          ))}
        </div>

        {aba === 'dados' ? (
          <div
            role="tabpanel"
            id="painel-dados"
            aria-labelledby="aba-dados"
            className="flex flex-col gap-4"
          >
            <dl className="grid gap-3 sm:grid-cols-2">
              <Dado rotulo="Etapa">
                <StatusBadge kind="corretor" value={corretor.stage} />
              </Dado>
              <Dado rotulo="Situacao SUSEP">
                <StatusBadge kind="situacaoExterna" value={corretor.external_status} />
              </Dado>
              <Dado rotulo="Comercial responsavel">
                {corretor.commercial_owner || <Ausente />}
              </Dado>
              <Dado rotulo="Comissao acordada">
                {formatRate(corretor.commission_bps)}
              </Dado>
              <Dado rotulo="Forma de recebimento">
                {corretor.pix_kind ? (
                  (tiposDeChave[corretor.pix_kind] ?? corretor.pix_kind)
                ) : (
                  <Ausente />
                )}
              </Dado>
              <Dado rotulo="Chave">{corretor.pix_key || <Ausente />}</Dado>
              <Dado rotulo="E-mail">{corretor.email || <Ausente />}</Dado>
              <Dado rotulo="Telefone">{corretor.phone || <Ausente />}</Dado>
              <Dado rotulo="Codigo SUSEP">{corretor.susep_code || <Ausente />}</Dado>
              <Dado rotulo="Cadastrado em">{formatDate(corretor.created_at)}</Dado>
              <Dado rotulo="Inscricao municipal">
                {corretor.municipal_registration || <Ausente />}
              </Dado>
              <Dado rotulo="Conta para repasse">
                {descreveConta(corretor.bank_account) || <Ausente />}
              </Dado>
              <Dado rotulo="Responsavel financeiro">
                {corretor.financial_contact.name ? (
                  [corretor.financial_contact.name, corretor.financial_contact.email]
                    .filter(Boolean)
                    .join(', ')
                ) : (
                  <Ausente />
                )}
              </Dado>
            </dl>

            <div>
              <p className="mb-2 text-xs font-semibold text-ink-700">
                Produtos habilitados
              </p>
              {corretor.product_ids.length === 0 ? (
                <p className="text-xs text-ink-500">
                  Sem restricao declarada. O corretor opera qualquer produto da
                  carteira.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {habilitados.map((produto) => (
                    <Badge key={produto.id} tone="neutral">
                      {produto.name}
                    </Badge>
                  ))}
                  {listaProdutos.isPending ? (
                    <span className="text-2xs text-ink-400">Carregando...</span>
                  ) : null}
                </div>
              )}
            </div>

            {podeAtualizar && corretor.next_stages.length > 0 ? (
              <div className="border-t border-line pt-3">
                <p className="mb-2 text-xs font-semibold text-ink-700">
                  Mover na esteira de ativacao
                </p>
                <div className="flex flex-wrap gap-2">
                  {corretor.next_stages.map((etapa) => (
                    <Button
                      key={etapa}
                      size="sm"
                      variant={etapa === 'bloqueado' ? 'danger' : 'secondary'}
                      loading={movendo}
                      onClick={() => onMover(etapa)}
                    >
                      {statusLabel('corretor', etapa)}
                    </Button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {aba === 'operacoes' ? (
          <div role="tabpanel" id="painel-operacoes" aria-labelledby="aba-operacoes">
            <Lista
              carregando={listaOperacoes.isPending}
              vazio="Sem operacoes"
              descricaoVazio="Nenhuma operacao foi aberta por este corretor."
              itens={listaOperacoes.data?.items ?? []}
              chave={(operacao) => operacao.id}
              linha={(operacao) => (
                <>
                  <div className="min-w-0">
                    <p className="truncate text-xs font-medium text-ink-900">
                      {operacao.subject || `Operacao ${operacao.number}`}
                    </p>
                    <p className="truncate text-2xs text-ink-500">
                      {operacao.policyholder.label}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-2xs tabular-nums text-ink-600">
                      {formatMoney(operacao.insured_amount)}
                    </span>
                    <StatusBadge kind="operacao" value={operacao.stage} />
                  </div>
                </>
              )}
            />
          </div>
        ) : null}

        {aba === 'comissoes' ? (
          <div role="tabpanel" id="painel-comissoes" aria-labelledby="aba-comissoes">
            <Lista
              carregando={listaComissoes.isPending}
              vazio="Sem comissoes"
              descricaoVazio="Nenhuma comissao foi lancada para este corretor."
              itens={listaComissoes.data?.items ?? []}
              chave={(comissao) => comissao.id}
              linha={(comissao) => (
                <>
                  <div className="min-w-0">
                    <p className="truncate text-xs font-medium text-ink-900">
                      {comissao.broker_label ||
                        (comissao.policy_id
                          ? `Apolice ${comissao.policy_id.slice(0, 8)}`
                          : 'Lancamento manual')}
                    </p>
                    <p className="truncate text-2xs text-ink-500">
                      Competencia {comissao.competence}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-2xs tabular-nums text-ink-600">
                      {formatMoney(comissao.amount)}
                    </span>
                    <StatusBadge kind="repasse" value={comissao.status} />
                  </div>
                </>
              )}
            />
          </div>
        ) : null}

        {aba === 'documentos' ? (
          <div role="tabpanel" id="painel-documentos" aria-labelledby="aba-documentos">
            <DocumentsPanel
              ownerType="corretor"
              ownerId={corretor.id}
              title="Documentos do corretor"
              group="Corretor"
              allowDelete
            />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

function Dado({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-2xs font-semibold tracking-wide text-ink-500 uppercase">
        {rotulo}
      </dt>
      <dd className="mt-0.5 text-xs text-ink-900">{children}</dd>
    </div>
  );
}

function Ausente() {
  return <span className="text-ink-400">nao informado</span>;
}

function Lista<T>({
  carregando,
  vazio,
  descricaoVazio,
  itens,
  chave,
  linha,
}: {
  carregando: boolean;
  vazio: string;
  descricaoVazio: string;
  itens: T[];
  chave: (item: T) => string;
  linha: (item: T) => React.ReactNode;
}) {
  if (carregando) {
    return (
      <div className="flex min-h-32 items-center justify-center">
        <Spinner size={20} label="Carregando" className="text-brand-500" />
      </div>
    );
  }
  if (itens.length === 0) {
    return <EmptyState icon="clock" title={vazio} description={descricaoVazio} />;
  }
  return (
    <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
      {itens.map((item) => (
        <li key={chave(item)} className="flex items-center gap-3 px-3 py-2.5">
          {linha(item)}
        </li>
      ))}
    </ul>
  );
}
